import { createDirectStore, } from 'direct-vuex';
import Vue from 'vue';
import Vuex from 'vuex';
import { ConfirmMoveoutAnonymousResponse, } from '~/api/self-serve-client';
import { AddressData, isAddressData, } from '~/utils/models';
import { isDate, isObject, } from '~/utils/models/isObject';
import { initialiseStoreValue, } from '~/utils/store';
import alertModule from '~/utils/store/alert-module';
import globalConfigModule from '~/utils/store/global-config-module';

Vue.use(Vuex);

const UserDetailsStorageKey = 'userDetails';
const UsermoveOutDetailsStorageKey = 'userMoveOutDetails';
const AddressEntryStorageKey = 'userMoveOutAddressEntry';
const MoveOutDateStorageKey = 'userMoveOutDate';
const CurrentMeterReadStorageKey = 'userMeterRead';

interface UserDetails {
	customerFirstName?: string;
	customerLastName: string;
	customerEmail: string;
	customerNumber: number;
	customerRefNumber: string;
	customerPaymentMethod: string;
}

interface MoveOutDetails {
	id: string;
	category: string;
	lastReading: number;
	lastReadDate: Date | string;
}

type AddressEntryMode = 'find' | 'manual';

type AddressEntryState = {
	mode: AddressEntryMode,
	address?: AddressData,
};

export interface UserMoveOutDetails {
	contractId: string;
	propertyAddress: AddressData;
	meter: MoveOutDetails;
}

export interface OverlayData {
	navigateTo: string;
	message: string;
}

export interface MoveOutState {
	isOverlayVisible: boolean;
	overlayMessage: string;
	navigateTo: string;
	customerMeterType: string;
	currentStep: 1 | 2 | 3 | 4 | 5 | null;
	showSpinner: boolean;
	userDetails: UserDetails | null;
	moveOutDetails: UserMoveOutDetails | undefined;
	currentMeterReading: number | null;
	userMoveOutDate: Date | null;
	addressEntry: AddressEntryState;
	addressList: AddressData[];
	isMoveoutForcefullySucceeded: null | boolean;
	finalBillAmount: number | undefined;
}

function isUserDetails(input: unknown): input is UserDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.customerLastName === 'string' &&
		typeof input.customerEmail === 'string' &&
		typeof input.customerNumber === 'number' &&
		typeof input.customerRefNumber === 'string' &&
		true
	);
}

function isMeterDetails(input: unknown): input is MoveOutDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.id === 'string' &&
		typeof input.category === 'string' &&
		typeof input.lastReading === 'number' &&
		isDate(input.lastReadDate) &&
		true
	);
}

function isUserMoveOutDetails(input: unknown): input is UserMoveOutDetails {
	return isObject(input) &&
		typeof input.contractId === 'string' &&
		isAddressData(input.propertyAddress) &&
		isMeterDetails(input.meter);
}

function isAddressEntry(input: unknown): input is AddressEntryState {
	if (!isObject(input)) {
		return false;
	}

	if (!input.mode) {
		return false;
	}

	if (input.address !== undefined && !isAddressData(input.address)) {
		return false;
	}

	if (input.mode === 'find') {
		return input.postcode === undefined || typeof input.postcode === 'string';
	} else if (input.mode === 'manual') {
		return true;
	}

	return false;
}

function initialiseUserDetails(): UserDetails | null {
	return initialiseStoreValue(UserDetailsStorageKey, isUserDetails, () => null);
}

function initialiseUserMoveOutDetails() {
	return initialiseStoreValue(UsermoveOutDetailsStorageKey,
		isUserMoveOutDetails,
		() => undefined,
		(userInput) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			userInput.meter.lastReadDate = new Date(userInput.meter.lastReadDate);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return userInput;
		});
}

function initialiseAddressEntryState(): AddressEntryState {
	return initialiseStoreValue(AddressEntryStorageKey, isAddressEntry, () => ({
		mode: 'find',
	}));
}

function initialiseMoveOutDate() : Date | null {
	return initialiseStoreValue(MoveOutDateStorageKey, isDate, () => null, (v) => new Date(v));
}

function initialiseCurrentReading() : number | null {
	return initialiseStoreValue(CurrentMeterReadStorageKey, (v): v is number => typeof v === 'string' && !isNaN(parseInt(v, 10)), () => null);
}

const { store, } = createDirectStore({
	state: {
		currentStep: null as (number | null),
		showSpinner: false,
		userDetails: initialiseUserDetails(),
		moveOutDetails: initialiseUserMoveOutDetails(),
		currentMeterReading: initialiseCurrentReading(),
		userMoveOutDate: initialiseMoveOutDate(),
		addressEntry: initialiseAddressEntryState(),
		customerMeterType: null as (string | null),
		addressList: [] as AddressData[],
		isMoveoutForcefullySucceeded: null,
		overlayMessage: '',
		navigateTo: '',
		isOverlayVisible: false,
	} as MoveOutState,
	getters: {
		hasExistingUserDetails(state) {
			return state.userDetails !== null;
		},
		hasExistingMoveOutDetails(state) {
			return state.moveOutDetails !== undefined;
		},
		hasForwardingAddress(state) {
			return isAddressEntry(state.addressEntry);
		},
	},
	mutations: {
		showSpinner(state) {
			state.showSpinner = true;
		},
		hideSpinner(state) {
			state.showSpinner = false;
		},
		setStep(state, step: MoveOutState['currentStep']) {
			state.currentStep = step;
		},
		updateUserDetails(state, userDetails: UserDetails) {
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(userDetails));
			state.userDetails = userDetails;
		},
		updateUserMeterDetails(state, userMeterDetails: UserMoveOutDetails) {
			sessionStorage.setItem(UsermoveOutDetailsStorageKey, JSON.stringify(userMeterDetails));
			state.moveOutDetails = userMeterDetails;
		},
		setMeterReading(state, meterReading: number) {
			state.currentMeterReading = meterReading;
			sessionStorage.setItem(CurrentMeterReadStorageKey, meterReading.toString(10));
		},
		checkCustomerMeterType(state, customerMeterType: string) {
			state.customerMeterType = customerMeterType;
		},
		setAddressList(state, list: AddressData[]) {
			state.addressList = list;
		},
		setMoveoutAnonymousResult(state, { isMoveoutForcefullySucceeded, finalBillAmount, }:ConfirmMoveoutAnonymousResponse) {
			state.isMoveoutForcefullySucceeded = isMoveoutForcefullySucceeded;
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			state.finalBillAmount = finalBillAmount;
		},
		closeOverlay(state) {
			state.isOverlayVisible = false;
		},
		showOverlay(state, data: OverlayData) {
			state.isOverlayVisible = true;
			state.overlayMessage = data.message;
			state.navigateTo = data.navigateTo;
		},
		reset(state) {
			sessionStorage.removeItem(UserDetailsStorageKey);
			sessionStorage.removeItem(UsermoveOutDetailsStorageKey);
			sessionStorage.removeItem(AddressEntryStorageKey);
			sessionStorage.removeItem(MoveOutDateStorageKey);
			sessionStorage.removeItem(CurrentMeterReadStorageKey);
			state.userDetails = initialiseUserDetails();
			state.moveOutDetails = initialiseUserMoveOutDetails();
			state.userMoveOutDate = initialiseMoveOutDate();
			state.currentMeterReading = null;
			state.addressEntry = initialiseAddressEntryState();
			state.addressList = [];
			state.isMoveoutForcefullySucceeded = null;
		},
		userMoveOutDate(state, moveOutDate: Date) {
			state.userMoveOutDate = moveOutDate;
			sessionStorage.setItem(MoveOutDateStorageKey, JSON.stringify(state.userMoveOutDate));
		},
		setAddressEntryMode(state, addressEntryMode: AddressEntryMode) {
			state.addressEntry = {
				...state.addressEntry,
				mode: addressEntryMode,
			};
			sessionStorage.setItem(AddressEntryStorageKey, JSON.stringify(state.addressEntry));
		},
		updateAddressData(state, address: AddressData) {
			state.addressEntry = {
				...state.addressEntry,
				address,
			};
			sessionStorage.setItem(AddressEntryStorageKey, JSON.stringify(state.addressEntry));
		},
	},
	modules: {
		alertModule,
		globalConfigModule,
	},
});

export const Store = store;
